import { useState, useMemo, useEffect, useRef, useCallback, type CSSProperties, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePermissions, handlePermissionClick } from '../../hooks/usePermissions';
import { type ColumnDef } from '@tanstack/react-table';
import { Plus, Pencil, Trash2, Users, Eye, GraduationCap, Clock, CheckCircle2, LayoutGrid, List, Search, ChevronLeft, ChevronRight, PowerOff, RotateCcw, Sparkles, AlertTriangle, RefreshCw, Save, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { useCrud } from '../../api/hooks/useCrud';
import { api } from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import { useQueryClient } from '@tanstack/react-query';
import { DataToolbar } from '../../components/ui/DataToolbar';
import { DataTable, PaginationFooter } from '../../components/ui/DataTable';
import { Modal } from '../../components/ui/Modal';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { RowActionMenu } from '../../components/ui/RowActionMenu';
import { hasPermission } from '../../auth/permissions';
import { SetupPageHero } from '../../components/onboarding/SetupPageHero';
import { BatchTurmasModal } from '../../components/onboarding/BatchTurmasModal';
import { GradeAssistant } from '../../components/grade/GradeAssistant';
import { TurmaDetalhePage, type TurmaGradeFooterChrome } from './TurmaDetalhe';
import type { Turma, Turno } from '../../types/models';
import { ConfigEntityFichaFooter } from '../../components/admin/config-entity-shared';
import { TurmaEditSidePanel } from '../../components/forms/TurmaEditSidePanel';
import { useHojeReferencia } from '../../api/hooks/useSimClock';
import { useHorarioAtual } from '../../api/hooks/useHorarios';
import { useStatusDia } from '../../api/hooks/useChamadas';
import '../../components/ui/datatable.css';
// Sub-módulos
import {
  SERIES_OPTIONS,
  nivelDaSerieLabel,
  rotuloTurmaCurto,
  turnoPreferidoPorHora,
  tipoAno,
  buildChamadaBadgesPorTurma,
  type ChamadaAulaBadge,
} from './turmas/turmasUtils';
import { ROUTES } from '../../api/routes';
import type { OcupacaoProfessorSlot } from './grade-conflito';
import { avaliarGradeTurma, type GradeStatusKind } from './turmas/turmasGradeStatus';
import { TurnosOciososReport } from './turnos/TurnosOciososReport';
import { useSetupStatus } from '../../hooks/useSetupStatus';

const anoAtual = new Date().getFullYear();

export function TurmasPage() {
  const { canManage: canManageFn } = usePermissions();
  const canManage = canManageFn('turmas');
  const { prontoParaOperar, etapas } = useSetupStatus();
  const isGradeBlocked = etapas?.find((e) => e.id === 'grade')?.status === 'bloqueada';
  const shouldShowGrade = prontoParaOperar || !isGradeBlocked;

  const { data: turmas, isLoading, error: loadError, create, update, deactivate } = useCrud<Turma>('turmas');
  const { data: turnos } = useCrud<Turno>('turnos');
  const escolaId = useAuthStore((s) => s.escolaId);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const basePath = '/' + (location.pathname.split('/')[1] || 'admin');
  const isAdmin = basePath === '/admin';
  const user = useAuthStore(s => s.user);

  // Chamada do dia (relógio sim / civil) — badges 1..N na lista
  const { hojeStr, isSimulation } = useHojeReferencia();
  const { horariosHoje } = useHorarioAtual(undefined, hojeStr);
  const { chamadasDia } = useStatusDia(undefined, hojeStr);
  const chamadaBadgesByTurma = useMemo(
    () => buildChamadaBadgesPorTurma(horariosHoje, chamadasDia),
    [horariosHoje, chamadasDia],
  );
  const dataChamadaLabel = useMemo(() => {
    const [y, m, d] = hojeStr.split('-').map(Number);
    const dt = new Date(y, (m || 1) - 1, d || 1);
    return new Intl.DateTimeFormat('pt-BR', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
    }).format(dt);
  }, [hojeStr]);
  // ── Navegação contextual ──
  const [selectedAno, setSelectedAno] = useState(anoAtual);
  const [selectedTurnoId, setSelectedTurnoId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'lista' | 'cards'>(() => (typeof window !== 'undefined' && window.innerWidth < 768 ? 'cards' : 'lista'));
  const [pageSize, setPageSize] = useState(10);
  const isSearchMode = searchQuery.trim().length > 0;

  // Controla se o turno já foi auto-selecionado para o ano atual
  // Evita sobrescrever a escolha manual do usuário a cada re-render
  const autoSelectedAnoRef = useRef<number | null>(null);

  // ── Modal / form ──
  const [modalOpen, setModalOpen] = useState(false);
  const [fichaId, setFichaId] = useState<string | null>(null);
  const [gradeFooterChrome, setGradeFooterChrome] = useState<TurmaGradeFooterChrome | null>(null);
  const onGradeFooterChange = useCallback((chrome: TurmaGradeFooterChrome | null) => {
    setGradeFooterChrome(chrome);
  }, []);
  const [editing, setEditing] = useState<Turma | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<Turma | null>(null);
  const [form, setForm] = useState({ nome: '', serie: '', turnoOperacionalId: '', anoLetivo: anoAtual, sala: '', capacidade: 35 });
  const [formErrors, setFormErrors] = useState<Partial<Record<'nome' | 'serie' | 'turnoOperacionalId' | 'anoLetivo', string>>>({});
  const [batchOpen, setBatchOpen] = useState(false);
  const [gradeAssistantOpen, setGradeAssistantOpen] = useState(false);
  const [gradeInitialTurmaId, setGradeInitialTurmaId] = useState<string | null>(null);
  const [ocupacao, setOcupacao] = useState<OcupacaoProfessorSlot[]>([]);
  const openGrade = (turmaId?: string) => {
    setGradeInitialTurmaId(turmaId ?? null);
    setGradeAssistantOpen(true);
  };

  useEffect(() => {
    if (!escolaId) return;
    let cancelled = false;
    api
      .get(ROUTES.horarios.ocupacao(escolaId))
      .then(({ data }) => {
        if (!cancelled) setOcupacao(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setOcupacao([]);
      });
    return () => {
      cancelled = true;
    };
  }, [escolaId, turmas]);

  const gradeChipStyle = (kind: GradeStatusKind, clickable: boolean): CSSProperties => {
    const base: CSSProperties = {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 3,
      fontSize: '0.62rem',
      fontWeight: 700,
      padding: '2px 7px',
      borderRadius: 999,
      border: '1px solid transparent',
      whiteSpace: 'nowrap',
      cursor: clickable ? 'pointer' : 'default',
      lineHeight: 1.2,
    };
    if (kind === 'OK') {
      return { ...base, background: '#dcfce7', color: '#15803d', borderColor: '#86efac' };
    }
    if (kind === 'GAPS') {
      return { ...base, background: '#fef3c7', color: '#b45309', borderColor: '#fcd34d' };
    }
    if (kind === 'CONFLITO') {
      return { ...base, background: '#fee2e2', color: '#b91c1c', borderColor: '#fca5a5' };
    }
    return { ...base, background: '#f1f5f9', color: '#64748b', borderColor: '#e2e8f0' };
  };

  const renderChamadaBadges = (turmaId: string): ReactNode => {
    const badges = chamadaBadgesByTurma.get(turmaId) ?? [];
    if (badges.length === 0) {
      return (
        <span style={{ fontSize: '0.72rem', color: '#94a3b8' }} title="Sem aulas na grade deste dia">
          —
        </span>
      );
    }
    return (
      <div
        style={{ display: 'inline-flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}
        title={`Chamada ${dataChamadaLabel}${isSimulation ? ' (simulado)' : ''}`}
      >
        {badges.map((b: ChamadaAulaBadge) => (
          <span
            key={b.numero}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 18,
              height: 18,
              padding: '0 4px',
              borderRadius: 999,
              fontSize: '0.62rem',
              fontWeight: 800,
              lineHeight: 1,
              background: b.feita ? '#dcfce7' : '#fee2e2',
              color: b.feita ? '#15803d' : '#b91c1c',
              border: `1px solid ${b.feita ? '#86efac' : '#fca5a5'}`,
            }}
            title={b.feita ? `${b.numero}ª aula — chamada feita` : `${b.numero}ª aula — pendente`}
          >
            {b.numero}
          </span>
        ))}
      </div>
    );
  };

  const renderStatusCell = (t: Turma) => {
    const av = avaliarGradeTurma(t, ocupacao);
    const Icon =
      av.kind === 'OK' ? CheckCircle2 : av.kind === 'SEM_GRADE' ? Clock : AlertTriangle;
    const gradeOk = av.kind === 'OK';
    // Grade OK = status suficiente; Ativa fica implícita. Inativa / problemas de grade abrem assistente.
    const showAtivoBadge = !t.ativo || !gradeOk;
    const chipClickable = t.ativo && !gradeOk;
    const chipTitle =
      av.detalhe +
      (av.semAlunos ? ' · Sem alunos' : '') +
      (chipClickable ? ' · Clique para abrir o assistente' : '');
    const chipStyle = {
      ...gradeChipStyle(av.kind, chipClickable),
      opacity: t.ativo ? 1 : 0.55,
    };
    const chipBody = (
      <>
        <Icon size={11} aria-hidden />
        {av.label}
        {av.kind === 'GAPS' && av.totalSlots > 0 ? ` ${av.comProfessor}/${av.totalSlots}` : null}
      </>
    );
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
        {showAtivoBadge && (
          <span className={`badge ${t.ativo ? 'badge-active' : 'badge-inactive'}`}>
            {t.ativo ? 'Ativa' : 'Inativa'}
          </span>
        )}
        {chipClickable ? (
          <button
            type="button"
            style={chipStyle}
            title={chipTitle}
            onClick={(e) => {
              e.stopPropagation();
              openGrade(t.id);
            }}
          >
            {chipBody}
          </button>
        ) : (
          <span style={chipStyle} title={chipTitle}>
            {chipBody}
          </span>
        )}
        {av.semAlunos && t.ativo && (
          <span
            style={{
              fontSize: '0.58rem',
              fontWeight: 600,
              color: '#94a3b8',
            }}
            title="Turma sem alunos vinculados"
          >
            Sem alunos
          </span>
        )}
      </div>
    );
  };

  // ── Lógica de dados ──
  const turnoAtivos = (turnos ?? []).filter(t => t.ativo);
  const turmasAtivas = (turmas ?? []).filter(t => t.ativo);

  const anosDisponiveis = useMemo(() => {
    const anos = [...new Set((turmas ?? []).map(t => t.anoLetivo))].sort();
    const proximo = anoAtual + 1;
    if (!anos.includes(proximo)) anos.push(proximo);
    if (!anos.includes(anoAtual)) anos.unshift(anoAtual);
    return anos;
  }, [turmas]);

  const turnosDoAno = useMemo(() =>
    turnoAtivos.filter(turno =>
      (turmas ?? []).some(t => t.anoLetivo === selectedAno && t.turnoOperacionalId === turno.id)
    ), [turmas, turnoAtivos, selectedAno]);

  // Turno preferido pela hora do dia (indicador ★ fixo)
  const turnoAtualId = useMemo(() =>
    turnoPreferidoPorHora(turnosDoAno)?.id ?? null
  , [turnosDoAno]);

  // Auto-seleção de turno por hora — apenas uma vez por ano selecionado
  useEffect(() => {
    if (!turnosDoAno.length) { setSelectedTurnoId(null); return; }
    // Já auto-selecionou para este ano → não sobrescreve escolha manual do usuário
    if (autoSelectedAnoRef.current === selectedAno) return;
    const pref = turnoPreferidoPorHora(turnosDoAno);
    setSelectedTurnoId(pref?.id ?? turnosDoAno[0].id);
    autoSelectedAnoRef.current = selectedAno;
  }, [turnosDoAno, selectedAno]);

  const getTurnoNome = (t: Turma) =>
    t.turnoOperacional?.nome || turnoAtivos.find(x => x.id === t.turnoOperacionalId)?.nome || '—';

  // Turmas filtradas
  const turmasFiltradas = useMemo(() => {
    if (isSearchMode) {
      const q = searchQuery.toLowerCase();
      return (turmas ?? []).filter(t => t.ativo &&
        (t.nome.toLowerCase().includes(q) || t.serie.toLowerCase().includes(q) || getTurnoNome(t).toLowerCase().includes(q))
      );
    }
    return (turmas ?? []).filter(t =>
      t.anoLetivo === selectedAno &&
      (!selectedTurnoId || t.turnoOperacionalId === selectedTurnoId) &&
      t.ativo
    );
  }, [turmas, isSearchMode, searchQuery, selectedAno, selectedTurnoId]);

  useEffect(() => {
    if (loadError) {
      toast.error('Não foi possível carregar as turmas. Tente atualizar a página.');
    }
  }, [loadError]);

  const turmaFicha = fichaId ? (turmas ?? []).find((t) => t.id === fichaId) : undefined;

  const isFuturoVazio = !isSearchMode && tipoAno(selectedAno, anoAtual) === 'futuro' && turmasFiltradas.length === 0;
  const semTurnosAtivos = turnoAtivos.length === 0;
  const semTurmasAtivas = turmasAtivas.length === 0;

  // ── Ações ──
  const openNew = () => { setEditing(null); setFormErrors({}); setForm({ nome: '', serie: '', turnoOperacionalId: turnoAtivos[0]?.id || '', anoLetivo: selectedAno, sala: '', capacidade: 35 }); setModalOpen(true); };
  const openEdit = (t: Turma) => { setEditing(t); setFormErrors({}); setForm({ nome: t.nome, serie: t.serie, turnoOperacionalId: t.turnoOperacionalId || '', anoLetivo: t.anoLetivo, sala: t.sala || '', capacidade: t.capacidade ?? 35 }); setModalOpen(true); };

  const handleSave = async () => {
    const nextErrors: Partial<Record<'nome' | 'serie' | 'turnoOperacionalId' | 'anoLetivo', string>> = {};
    if (!form.nome.trim()) nextErrors.nome = 'Informe o nome da turma.';
    if (!form.serie.trim()) nextErrors.serie = 'Selecione a serie.';
    if (!form.turnoOperacionalId) nextErrors.turnoOperacionalId = 'Selecione o turno operacional.';
    if (!Number.isFinite(form.anoLetivo) || form.anoLetivo < 2020 || form.anoLetivo > 2040) {
      nextErrors.anoLetivo = 'Informe um ano letivo valido (2020 a 2040).';
    }
    if (Object.keys(nextErrors).length > 0) {
      setFormErrors(nextErrors);
      return;
    }

    const nomeNormalizado = form.nome.trim().toLowerCase();
    const serieNormalizada = form.serie.trim().toLowerCase();
    const duplicada = (turmas ?? []).find((t) =>
      t.id !== editing?.id &&
      t.nome.trim().toLowerCase() === nomeNormalizado &&
      t.serie.trim().toLowerCase() === serieNormalizada &&
      t.anoLetivo === form.anoLetivo &&
      (t.turnoOperacionalId || '') === (form.turnoOperacionalId || '')
    );
    if (duplicada) {
      setFormErrors({
        nome: 'Ja existe turma com mesmo nome, serie, ano letivo e turno. Altere o nome (ex.: 1º Ano A/B) ou ajuste turno/ano.',
      });
      return;
    }
    setFormErrors({});
    try {
      if (editing) { 
        await update.mutateAsync({ id: editing.id, ...form } as any); 
        toast.success('Turma atualizada!'); 
      } else { 
        await create.mutateAsync(form as any); 
        toast.success('Turma criada!'); 
      }
      qc.invalidateQueries({ queryKey: ['turmas'] });
      window.dispatchEvent(new CustomEvent('ac-data-refetch'));
      setModalOpen(false);
    } catch (e: any) {
      const rawMsg =
        e?.response?.data?.error?.message ||
        e?.response?.data?.message ||
        e?.message ||
        '';
      const normalized = String(rawMsg).toLowerCase();

      if (e?.response?.status === 409 || normalized.includes('ja existe')) {
        setFormErrors({
          nome: 'Ja existe turma com mesmo nome, serie, ano letivo e turno. Altere o nome (ex.: 1º Ano A/B) ou ajuste turno/ano.',
        });
        return;
      }

      toast.error('Nao foi possivel salvar a turma. Revise os campos obrigatorios e tente novamente.');
    }
  };

  const requestDelete = (id: string) => {
    const turma = (turmas ?? []).find(t => t.id === id);
    if (!turma) return;
    const alunoCount = turma._count?.alunos || 0;
    if (turma.ativo && alunoCount > 0) {
      toast.error(`Não é possível desativar "${turma.nome}" — ${alunoCount} aluno(s) vinculado(s). Transfira primeiro.`, { duration: 6000 });
      return;
    }
    setDeleteConfirm(turma);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    const targetTurma = deleteConfirm;
    setDeleteConfirm(null);
    const id = targetTurma.id;
    const turma = (turmas ?? []).find(t => t.id === id);
    if (!turma) return;
    const alunoCount = turma._count?.alunos || 0;
    if (turma.ativo && alunoCount > 0) { toast.error(`Não é possível desativar "${turma.nome}" — ${alunoCount} aluno(s) vinculado(s). Transfira primeiro.`, { duration: 6000 }); return; }
    if (!turma.ativo && alunoCount === 0) {
      try { 
        await api.delete(ROUTES.turmas.hardDelete(escolaId!, id)); 
        toast.success('Turma removida'); 
        qc.invalidateQueries({ queryKey: ['turmas'] }); 
        window.dispatchEvent(new CustomEvent('ac-data-refetch'));
      } catch (e: any) { 
        toast.error(e?.response?.data?.message || 'Erro'); 
      }
    } else {
      try {
        await deactivate.mutateAsync(id);
        toast.success('Turma desativada');
        qc.invalidateQueries({ queryKey: ['turmas'] });
        window.dispatchEvent(new CustomEvent('ac-data-refetch'));
      } catch (e: any) {
        const rawMsg = e?.response?.data?.error?.message || e?.response?.data?.message || e?.message || 'Erro ao desativar';
        toast.error(rawMsg);
      }
    }
  };

  const handleAtivar = async (id: string) => {
    try {
      await api.put(ROUTES.turmas.atualizar(escolaId!, id), { ativo: true });
      toast.success('Turma reativada!');
      qc.invalidateQueries({ queryKey: ['turmas'] });
      window.dispatchEvent(new CustomEvent('ac-data-refetch'));
    } catch (e: any) {
      const rawMsg = e?.response?.data?.error?.message || e?.response?.data?.message || e?.message || 'Erro ao reativar turma';
      toast.error(rawMsg);
    }
  };

  // Série · Turma · Nível · Turno · Sala · Alunos · Status · Ações
  // (ano nos filtros; registro/auditoria na ficha)
  const columnsSearch: ColumnDef<Turma>[] = [
    { accessorKey: 'serie', header: 'Série' },
    {
      id: 'turma',
      header: 'Turma',
      accessorFn: (row) => rotuloTurmaCurto(row),
      cell: ({ row }) => (
        <span title={row.original.nome} style={{ fontWeight: 700 }}>
          {rotuloTurmaCurto(row.original)}
        </span>
      ),
    },
    {
      id: 'nivel',
      header: 'Nível',
      accessorFn: (row) => nivelDaSerieLabel(row.serie),
      cell: ({ row }) => (
        <span style={{ color: '#64748b', fontSize: '0.78rem', fontWeight: 600 }}>
          {nivelDaSerieLabel(row.original.serie)}
        </span>
      ),
    },
    {
      id: 'turno',
      header: 'Turno',
      accessorFn: (row) => getTurnoNome(row),
      cell: ({ row }) => getTurnoNome(row.original),
    },
    {
      id: 'sala',
      header: 'Sala',
      cell: ({ row }) => row.original.sala?.trim() || '—',
    },
    {
      id: 'alunos',
      header: 'Alunos',
      cell: ({ row }) => {
        const count = row.original._count?.alunos ?? 0;
        const cap = row.original.capacidade ?? 0;
        return (
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Users size={13} /> {count}{cap > 0 ? `/${cap}` : ''}
          </span>
        );
      },
    },
    {
      id: 'chamada',
      header: () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, lineHeight: 1.15 }}>
          <span>Chamada</span>
          <span
            style={{
              fontSize: '0.6rem',
              fontWeight: 700,
              color: isSimulation ? '#7c3aed' : '#64748b',
              textTransform: 'capitalize',
            }}
            title={isSimulation ? `Dia simulado ${hojeStr}` : `Dia ${hojeStr}`}
          >
            {dataChamadaLabel}
            {isSimulation ? ' · sim' : ''}
          </span>
        </div>
      ),
      cell: ({ row }) => renderChamadaBadges(row.original.id),
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => renderStatusCell(row.original),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const t = row.original;
        const alunoCount = t._count?.alunos || 0;
        const bloqueado = t.ativo && alunoCount > 0;
        return (
          <div className="flex items-center" style={{ opacity: t.ativo ? 1 : 0.4 }}>
            {/* Mobile: 3 pontinhos com menu flutuante */}
            <div className="flex md:hidden">
              <RowActionMenu
                items={[
                  {
                    label: 'Ver Detalhes',
                    icon: <Eye size={15} />,
                    onClick: () => setFichaId(t.id),
                    variant: 'view',
                  },
                  canManage && t.ativo
                    ? {
                        label: 'Editar Turma',
                        icon: <Pencil size={15} />,
                        onClick: () => openEdit(t),
                        variant: 'edit',
                      }
                    : null,
                  canManage && !t.ativo
                    ? {
                        label: 'Reativar Turma',
                        icon: <RotateCcw size={15} />,
                        onClick: () => handleAtivar(t.id),
                        variant: 'success',
                      }
                    : null,
                  canManage && t.ativo
                    ? {
                        label: bloqueado ? 'Desativar Turma (tem alunos)' : 'Desativar Turma',
                        icon: <PowerOff size={15} />,
                        onClick: () => requestDelete(t.id),
                        disabled: bloqueado,
                        variant: 'danger',
                      }
                    : null,
                ]}
              />
            </div>

            {/* Desktop: Ações diretas */}
            <div className="hidden md:flex items-center gap-1">
              <button className="action-btn" onClick={() => setFichaId(t.id)} title="Ver Detalhes"><Eye size={14} /></button>
              {t.ativo && (
                <button
                  className={`action-btn ${!canManage ? 'opacity-40 cursor-not-allowed' : ''}`}
                  onClick={() => handlePermissionClick(canManage, () => openEdit(t), 'Você não tem permissão para editar turmas.')}
                  title={!canManage ? 'Permissão restrita para edição' : 'Editar'}
                >
                  <Pencil size={14} />
                </button>
              )}
              {!t.ativo && (
                <button
                  className={`action-btn ${!canManage ? 'opacity-40 cursor-not-allowed' : ''}`}
                  onClick={() => handlePermissionClick(canManage, () => handleAtivar(t.id), 'Você não tem permissão para reativar turmas.')}
                  style={{ color: 'var(--success-color, #10b981)' }}
                  title={!canManage ? 'Permissão restrita para reativação' : 'Reativar'}
                >
                  <RotateCcw size={14} />
                </button>
              )}
              {t.ativo && (
                <button
                  className={`action-btn ${!canManage || bloqueado ? 'opacity-40 cursor-not-allowed' : 'action-btn-danger'}`}
                  onClick={() => {
                    if (bloqueado) {
                      toast.error('Turma possui alunos vinculados. Desvincule os alunos antes de desativar.');
                      return;
                    }
                    handlePermissionClick(canManage, () => requestDelete(t.id), 'Você não tem permissão para desativar turmas.');
                  }}
                  style={bloqueado ? { opacity: 0.5 } : { color: 'var(--danger-color, #ef4444)' }}
                  title={bloqueado ? 'Turma possui alunos matriculados' : (!canManage ? 'Permissão restrita para desativação' : 'Desativar')}
                >
                  <PowerOff size={14} />
                </button>
              )}
            </div>
          </div>
        );
      },
    },
  ];

  const selectedTurno = turnoAtivos.find(t => t.id === form.turnoOperacionalId);
  const templatePreview = selectedTurno?.horariosTemplate ?? [];

  return (
    <div className="admin-page ac-turmas-page" style={{ marginTop: -24, display: 'flex', flexDirection: 'column', gap: 0 }}>
      {/* Hero guia (padrão compacto) */}
      {!isLoading && (
        <SetupPageHero
          icon={GraduationCap}
          etapa={4}
          titulo="Turmas"
          descricao={
            semTurnosAtivos
              ? 'Configure primeiro os turnos para habilitar a criação de turmas.'
              : semTurmasAtivas
                ? `Crie as turmas da escola. Turnos disponíveis: ${turnoAtivos.map((t) => t.nome).join(', ')}.`
                : 'Organize as turmas por ano e turno. Use a lista abaixo para editar, desativar ou abrir grade.'
          }
          bloqueado={semTurnosAtivos}
          mensagemBloqueio={semTurnosAtivos ? 'Configure os Turnos antes de criar Turmas' : undefined}
          acoes={[]}
          dismissivel={false}
          compact
        />
      )}

      <div
        className="ac-admin-data-card ac-turmas-data-card"
        style={{ background: 'var(--bg-card, white)', border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 12, display: 'flex', flexDirection: 'column' }}
      >
        <DataToolbar
          busca={searchQuery}
          onBuscaChange={setSearchQuery}
          placeholder="Buscar turma..."
          actions={
            <>
              <button
                type="button"
                className={`btn-primary ${!canManage ? 'opacity-50 cursor-not-allowed filter grayscale' : ''}`}
                onClick={() => handlePermissionClick(canManage, openNew, 'Você não tem permissão para criar turmas.')}
                title={!canManage ? 'Permissão restrita a Diretores e Administradores' : 'Criar nova turma'}
                style={{ height: 34, padding: '0 12px', fontSize: '.8rem', display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0 }}
              >
                <Plus size={15} /> Nova turma
              </button>
              <button
                type="button"
                className={`btn-primary ${!canManage ? 'opacity-50 cursor-not-allowed filter grayscale' : ''}`}
                onClick={() => handlePermissionClick(canManage, () => setBatchOpen(true), 'Você não tem permissão para usar o assistente de criação.')}
                title={!canManage ? 'Permissão restrita a Diretores e Administradores' : 'Assistente de Criação em Lote'}
                style={{
                  height: 34,
                  padding: '0 12px',
                  fontSize: '.78rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  borderRadius: 8,
                  fontWeight: 600,
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                  border: 'none',
                  color: '#fff',
                  boxShadow: '0 2px 8px rgba(139,92,246,0.25)',
                }}
              >
                <Sparkles size={14} aria-hidden /> Assistente de Criação
              </button>
              <button
                type="button"
                data-testid="btn-abrir-grade-horaria"
                className="btn-primary"
                onClick={() => openGrade()}
                title="Montagem e otimização da Grade Horária Semanal"
                style={{
                  height: 34,
                  padding: '0 12px',
                  fontSize: '.78rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  borderRadius: 8,
                  fontWeight: 600,
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  background: 'linear-gradient(135deg, #059669, #10b981)',
                  border: 'none',
                  color: '#fff',
                  boxShadow: '0 2px 8px rgba(16,185,129,0.25)',
                }}
              >
                <Calendar size={14} aria-hidden /> Grade Horária
              </button>
            </>
          }
          extra={
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                fontSize: '.72rem',
                fontWeight: 700,
                padding: '4px 9px',
                borderRadius: 8,
                background: isSimulation ? '#f5f3ff' : '#f1f5f9',
                color: isSimulation ? '#6d28d9' : '#475569',
                border: `1px solid ${isSimulation ? '#ddd6fe' : '#e2e8f0'}`,
                textTransform: 'capitalize',
                flexShrink: 0,
              }}
              title={isSimulation ? `Chamada do dia simulado ${hojeStr}` : `Chamada do dia ${hojeStr}`}
            >
              <Calendar size={12} aria-hidden />
              {isSimulation ? 'Dia sim' : 'Hoje'}: {dataChamadaLabel}
            </span>
          }
          totalRecords={turmasFiltradas.length}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          pageSizeOptions={[10, 20, 40, 100]}
          viewMode={viewMode}
          onViewModeChange={(m) => setViewMode(m as 'lista' | 'cards')}
          viewModes={['lista', 'cards']}
        />

        {/* Filtro de turno */}
        {!isSearchMode && turnosDoAno.length > 0 && (
          <div style={{
            display: 'flex', gap: 0, padding: '0 10px',
            borderBottom: '1px solid var(--border-color, #e5e7eb)',
            background: 'var(--bg-main, #f8fafc)',
            overflowX: 'auto',
          }}>
            <button type="button" onClick={() => setSelectedTurnoId(null)} style={{
              padding: '8px 12px', fontSize: '.74rem', fontWeight: selectedTurnoId === null ? 700 : 500,
              border: 'none', background: 'none', cursor: 'pointer',
              borderBottom: selectedTurnoId === null ? '2px solid #2563eb' : '2px solid transparent',
              color: selectedTurnoId === null ? '#2563eb' : 'var(--text-muted, #64748b)',
              whiteSpace: 'nowrap',
            }}>Todos</button>
            {turnosDoAno.map((turno) => (
              <button key={turno.id} type="button" onClick={() => setSelectedTurnoId(turno.id)} style={{
                padding: '8px 12px', fontSize: '.74rem', fontWeight: selectedTurnoId === turno.id ? 700 : 500,
                border: 'none', background: 'none', cursor: 'pointer',
                borderBottom: selectedTurnoId === turno.id ? '2px solid #2563eb' : '2px solid transparent',
                color: selectedTurnoId === turno.id ? '#2563eb' : 'var(--text-muted, #64748b)',
                whiteSpace: 'nowrap',
              }}>{turno.nome}{turno.id === turnoAtualId ? ' \u2605' : ''}</button>
            ))}
          </div>
        )}

        <div className="ac-admin-list-scroll" style={{ display: 'flex', flexDirection: 'column' }}>
          {/* Conteúdo principal */}
          {viewMode === 'lista' ? (
            <DataTable columns={columnsSearch} data={turmasFiltradas} isLoading={isLoading} searchPlaceholder="Buscar turma..." showToolbar={false} pageSize={pageSize} fixedHeight={false} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-2 sm:gap-3 p-2 sm:p-3">
              {turmasFiltradas.map((t) => {
                const alunoCount = t._count?.alunos ?? 0;
                const cap = t.capacidade ?? 0;
                const bloqueado = t.ativo && alunoCount > 0;
                return (
                  <div key={t.id} className="border border-[var(--border-color,#e5e7eb)] rounded-xl p-2.5 sm:p-3 bg-[var(--bg-card,white)] shadow-sm hover:shadow transition-shadow flex flex-col justify-between" style={{ opacity: t.ativo ? 1 : 0.45 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, gap: 8 }}>
                      <div style={{ minWidth: 0 }}>
                        <div className="font-bold text-xs sm:text-sm text-[var(--text-main,#1e293b)] truncate">{t.nome}</div>
                        <div className="text-[11px] text-blue-600 dark:text-blue-400 truncate">{t.serie} • {getTurnoNome(t)}</div>
                      </div>
                      <div className="scale-90 origin-right">{renderStatusCell(t)}</div>
                    </div>
                    <div className="text-[11px] text-slate-500 mb-1.5 truncate">
                      {nivelDaSerieLabel(t.serie)}
                      {t.sala ? ` · ${t.sala}` : ''}
                      {' · '}Alunos: {alunoCount}{cap > 0 ? `/${cap}` : ''}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, minHeight: 18 }}>
                      <span style={{ fontSize: '.62rem', fontWeight: 700, color: '#64748b' }}>Chamada</span>
                      {renderChamadaBadges(t.id)}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 4, borderTop: '1px solid var(--border-color,#e5e7eb)/50', paddingTop: 6 }}>
                      {/* Mobile: 3 pontinhos */}
                      <div className="flex md:hidden">
                        <RowActionMenu
                          items={[
                            {
                              label: 'Ver Detalhes',
                              icon: <Eye size={15} />,
                              onClick: () => setFichaId(t.id),
                              variant: 'view',
                            },
                            canManage && t.ativo
                              ? {
                                  label: 'Editar Turma',
                                  icon: <Pencil size={15} />,
                                  onClick: () => openEdit(t),
                                  variant: 'edit',
                                }
                              : null,
                            canManage && !t.ativo
                              ? {
                                  label: 'Reativar Turma',
                                  icon: <RotateCcw size={15} />,
                                  onClick: () => handleAtivar(t.id),
                                  variant: 'success',
                                }
                              : null,
                            canManage && t.ativo
                              ? {
                                  label: bloqueado ? 'Desativar Turma (tem alunos)' : 'Desativar Turma',
                                  icon: <PowerOff size={15} />,
                                  onClick: () => requestDelete(t.id),
                                  disabled: bloqueado,
                                  variant: 'danger',
                                }
                              : null,
                          ]}
                        />
                      </div>

                      {/* Desktop: Ações diretas */}
                      <div className="hidden md:flex items-center gap-1">
                        <button className="action-btn" title="Ver Detalhes" onClick={() => setFichaId(t.id)}><Eye size={13} /></button>
                        {canManage && t.ativo && <button className="action-btn" title="Editar" onClick={() => openEdit(t)}><Pencil size={13} /></button>}
                        {canManage && !t.ativo && <button className="action-btn" title="Reativar" onClick={() => handleAtivar(t.id)} style={{ color: 'var(--success-color, #10b981)' }}><RotateCcw size={13} /></button>}
                        {canManage && t.ativo && <button className={`action-btn ${bloqueado ? '' : 'action-btn-danger'}`} title={bloqueado ? 'Tem alunos vinculados' : 'Desativar/Remover'} onClick={() => requestDelete(t.id)} style={bloqueado ? { opacity: 0.5, cursor: 'not-allowed' } : { color: 'var(--danger-color, #ef4444)' }}><PowerOff size={13} /></button>}
                      </div>
                    </div>
                  </div>
                );
              })}
              {turmasFiltradas.length === 0 && <div style={{ color: 'var(--text-muted)', fontSize: '.85rem', padding: 8 }}>Nenhuma turma encontrada.</div>}
            </div>
          )}
          {viewMode === 'cards' && (
            <PaginationFooter
              totalItems={turmasFiltradas.length}
              pageSize={pageSize}
            />
          )}

          {!isLoading && turnoAtivos.length > 0 && prontoParaOperar && (
            <div style={{ padding: '0 12px 12px 12px' }}>
              <TurnosOciososReport
                turnos={turnos ?? []}
                selectedTurnoId={selectedTurnoId}
                turnoPreferidoId={turnoAtualId}
                onTurnoChange={setSelectedTurnoId}
              />
            </div>
          )}
        </div>
      </div>

      {/* Modal de criação/edição */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Editar Turma' : 'Nova Turma'}
        contentClassName={editing ? 'ac-turma-modal ac-config-modal--edit ac-turma-modal--edit' : 'ac-turma-modal'}
        dialogId="modal-nova-turma"
        footer={
          <>
            <button className="btn-cancel" onClick={() => setModalOpen(false)}>Cancelar</button>
            <button className="btn-save" onClick={handleSave} disabled={create.isPending || update.isPending}>
              {(create.isPending || update.isPending) ? 'Salvando...' : 'Salvar'}
            </button>
          </>
        }
      >
        <div className={editing ? 'ac-modal-dual ac-turma-modal-dual' : undefined}>
          <div className={editing ? 'ac-modal-dual__columns' : undefined}>
            <div className={editing ? 'ac-modal-dual__main' : undefined}>
              <div className="ac-turma-form-grid">
                <div className="form-group">
                  <label className="form-label">Nome da Turma *</label>
                  <input
                    id="turma-form-nome"
                    name="nome"
                    className="form-input"
                    value={form.nome}
                    onChange={e => {
                      const value = e.target.value;
                      setForm({ ...form, nome: value });
                      if (formErrors.nome) setFormErrors(prev => ({ ...prev, nome: undefined }));
                    }}
                    placeholder="Ex: 1º Ano A"
                  />
                  <p style={{ margin: '4px 0 0', fontSize: '0.68rem', color: '#94a3b8' }}>
                    Use série + letra da turma (A, B, C…). O turno fica no campo Turno.
                  </p>
                  {formErrors.nome && <p className="form-error">{formErrors.nome}</p>}
                </div>
                <div className="form-group">
                  <label className="form-label">Série *</label>
                  <select
                    id="turma-form-serie"
                    name="serie"
                    className="form-select"
                    value={form.serie}
                    onChange={e => { setForm({ ...form, serie: e.target.value }); if (formErrors.serie) setFormErrors(prev => ({ ...prev, serie: undefined })); }}
                  >
                    <option value="">Selecione...</option>
                    {SERIES_OPTIONS.map(g => <optgroup key={g.group} label={g.group}>{g.items.map(s => <option key={s} value={s}>{s}</option>)}</optgroup>)}
                  </select>
                  {formErrors.serie && <p className="form-error">{formErrors.serie}</p>}
                </div>
              </div>
              <div className="ac-turma-form-grid">
                <div className="form-group">
                  <label className="form-label">Turno Operacional *</label>
                  <select
                    id="turma-form-turno"
                    name="turnoOperacionalId"
                    className="form-select"
                    value={form.turnoOperacionalId}
                    onChange={e => { setForm({ ...form, turnoOperacionalId: e.target.value }); if (formErrors.turnoOperacionalId) setFormErrors(prev => ({ ...prev, turnoOperacionalId: undefined })); }}
                  >
                    <option value="">Selecione...</option>
                    {turnoAtivos.map(t => <option key={t.id} value={t.id}>{t.nome} ({t.horaEntradaOficial}–{t.horaSaidaOficial})</option>)}
                  </select>
                  {formErrors.turnoOperacionalId && <p className="form-error">{formErrors.turnoOperacionalId}</p>}
                </div>
                <div className="form-group">
                  <label className="form-label">Ano Letivo *</label>
                  <input
                    id="turma-form-ano-letivo"
                    name="anoLetivo"
                    className="form-input"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={4}
                    value={form.anoLetivo || ''}
                    onChange={e => {
                      const digits = e.target.value.replace(/\D/g, '');
                      setForm({ ...form, anoLetivo: digits ? parseInt(digits, 10) : 0 });
                      if (formErrors.anoLetivo) setFormErrors(prev => ({ ...prev, anoLetivo: undefined }));
                    }}
                    placeholder="Ex: 2026"
                  />
                  {formErrors.anoLetivo && <p className="form-error">{formErrors.anoLetivo}</p>}
                </div>
              </div>
              <div className="ac-turma-form-grid">
                <div className="form-group">
                  <label className="form-label">Sala</label>
                  <input
                    id="turma-form-sala"
                    name="sala"
                    className="form-input"
                    value={form.sala}
                    onChange={e => setForm({ ...form, sala: e.target.value })}
                    placeholder="Ex: Sala 101"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Capacidade</label>
                  <input
                    id="turma-form-capacidade"
                    name="capacidade"
                    className="form-input"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={3}
                    value={form.capacidade ? String(form.capacidade) : ''}
                    onChange={e => {
                      const digits = e.target.value.replace(/\D/g, '');
                      setForm({ ...form, capacidade: digits ? parseInt(digits, 10) : 0 });
                    }}
                    placeholder="Ex: 35"
                  />
                </div>
              </div>
              {form.turnoOperacionalId && templatePreview.length > 0 && (
                <div style={{ background: '#f0fdf4', borderRadius: 10, padding: 10, border: '1px solid #bbf7d0', marginTop: 4 }}>
                  <h4 style={{ fontSize: '0.74rem', fontWeight: 700, color: '#166534', margin: '0 0 7px' }}>✅ Horários herdados do turno "{selectedTurno?.nome}"</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {templatePreview.map(slot => <span key={slot.id || slot.numeroAula} style={{ fontSize: '0.66rem', padding: '2px 7px', borderRadius: 5, fontWeight: 600, background: slot.tipo === 'AULA' ? '#dbeafe' : '#fde68a', color: slot.tipo === 'AULA' ? '#1e40af' : '#92400e' }}>{slot.descricao || slot.tipo} {slot.horaInicio}–{slot.horaFim}</span>)}
                  </div>
                </div>
              )}
            </div>
            {editing && (
              <>
                <div className="ac-modal-dual__divider" aria-hidden />
                <TurmaEditSidePanel turma={editing} turnoNome={getTurnoNome(editing)} />
              </>
            )}
          </div>
        </div>
      </Modal>

      <BatchTurmasModal open={batchOpen} onClose={() => setBatchOpen(false)} turnos={turnoAtivos.map(t => ({ id: t.id, nome: t.nome }))} turmasAtivas={(turmas ?? []).filter(t => t.ativo).map(t => ({ nome: t.nome, serie: t.serie, turnoOperacionalId: t.turnoOperacionalId }))} turmasInativas={(turmas ?? []).filter(t => !t.ativo).map(t => ({ nome: t.nome, serie: t.serie, turnoOperacionalId: t.turnoOperacionalId }))} />
      <GradeAssistant open={gradeAssistantOpen} onClose={() => { setGradeAssistantOpen(false); setGradeInitialTurmaId(null); }} initialTurmaId={gradeInitialTurmaId} />
      <ConfirmModal
        open={!!deleteConfirm}
        title={deleteConfirm?.ativo ? 'Desativar turma' : 'Remover turma'}
        message={
          deleteConfirm
            ? deleteConfirm.ativo
              ? `Deseja desativar "${deleteConfirm.nome}"? A turma ficará indisponível até ser reativada.`
              : `Deseja remover permanentemente "${deleteConfirm.nome}"? Esta ação não pode ser desfeita.`
            : ''
        }
        confirmLabel={deleteConfirm?.ativo ? 'Desativar' : 'Remover'}
        cancelLabel="Cancelar"
        onCancel={() => setDeleteConfirm(null)}
        onConfirm={() => void confirmDelete()}
      />

      <Modal
        title=""
        open={!!fichaId && !!turmaFicha}
        onClose={() => setFichaId(null)}
        size="wide"
        contentClassName="ac-ficha-modal ac-turma-ficha-modal"
        hideCloseButton
        footer={
          turmaFicha ? (
            <ConfigEntityFichaFooter
              entity={turmaFicha}
              canManage={canManage}
              statusAfterMeta
              beforeMeta={
                gradeFooterChrome ? (
                  <button
                    type="button"
                    className={`resp-ficha-btn${gradeFooterChrome.semSlots ? ' resp-ficha-btn--primary' : ''}`}
                    onClick={gradeFooterChrome.onRedefinir}
                  >
                    {gradeFooterChrome.semSlots ? (
                      <>
                        <Calendar size={14} /> Criar Grade
                      </>
                    ) : (
                      <>
                        <RefreshCw size={14} /> Redefinir Turno
                      </>
                    )}
                  </button>
                ) : null
              }
              afterEdit={
                gradeFooterChrome ? (
                  <button
                    type="button"
                    className={`resp-ficha-btn resp-ficha-btn--primary${
                      gradeFooterChrome.semSlots || gradeFooterChrome.saving || gradeFooterChrome.temErros ? ' resp-ficha-btn--disabled' : ''
                    }`}
                    onClick={gradeFooterChrome.onSalvar}
                    disabled={gradeFooterChrome.semSlots || gradeFooterChrome.saving || gradeFooterChrome.temErros}
                  >
                    <Save size={14} />
                    {gradeFooterChrome.saving ? 'Salvando...' : 'Salvar'}
                  </button>
                ) : null
              }
              onEdit={() => {
                setFichaId(null);
                setGradeFooterChrome(null);
                openEdit(turmaFicha);
              }}
              onDeactivate={() => {
                setFichaId(null);
                setGradeFooterChrome(null);
                requestDelete(turmaFicha.id);
              }}
              onReactivate={() => {
                setFichaId(null);
                setGradeFooterChrome(null);
                void handleAtivar(turmaFicha.id);
              }}
            />
          ) : null
        }
      >
        {fichaId && (
          <TurmaDetalhePage
            overrideId={fichaId}
            onClose={() => {
              setFichaId(null);
              setGradeFooterChrome(null);
            }}
            onGradeFooterChange={onGradeFooterChange}
          />
        )}
      </Modal>
    </div>
  );
}
